import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@chakra-ui/react";
import usePrevious from "@/js/utils/usePrevious";
import { useScreenshareStore } from "@/stores/v2/screenshareStore";
import { useClassroomPolicyStore } from "@/stores/v2/classroomPolicyStore";
import Api from "@/js/api/authorisedApi";
import iconHelpBlue from "@/assets/IconHelpBlue.svg?url";
import { isBeingRestricted, WindowData } from "@/js/components/RegularClassroomUser/utils";
import StudentTileScreenshot from "@/js/components/StudentTile/components/StudentTileScreenshot";
import StudentTileTabView from "@/js/components/StudentTile/components/StudentTileTabView";
import InactiveStudentWindow from "@/js/components/StudentTile/components/InactiveStudentWindow";
import { envConfig } from "@/config";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { StudentScreenshotStatus, useStudentState } from "@/stores/v2/studentStateStore";
import { ClassroomDetails } from "@/stores/classroomStore_I";
import { IGetSignatureDbReturn } from "@/stores/signatureStore_I";
import { User } from "@/js/utils/userUtils";
import { useToast } from "@/components/ui/Toast";

interface StudentTileContentProps {
	user: User;
	windowData: WindowData | Record<string, never>;
	signatureDb: IGetSignatureDbReturn;
	view?: "screens" | "tabs";
	canCloseTab: boolean;
	isClassActive: boolean;
	classroom: ClassroomDetails;
	openP2pModal: (
		user: User,
		tabId: string | undefined,
		chromeId: string | undefined,
		windowId: string | undefined,
		isActiveTab: boolean
	) => void;
	tabItemLimit: number;
	disableScreenshotRetry?: boolean;
	classOpenTime?: number;
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const StudentTileContent = (props: StudentTileContentProps) => {
	const [isScreenshotLoading, setIsScreenshotLoading] = useState(false);
	const prevView = usePrevious(props.view);
	const prevWindowData = usePrevious(props.windowData);
	const closedTabId: React.MutableRefObject<string | undefined> = useRef();
	const screenshareState = useScreenshareStore();
	const deviceId = useUserSessionStore((state) => state.active_device);

	const hasWindowData = useMemo(() => {
		return props.windowData && Object.keys(props.windowData).length > 0;
	}, [props.windowData]);

	const [previousHasWindowData, setPreviousHasWindowData] = useState<undefined | boolean>(undefined);
	const [previousIsClassActive, setPreviousIsClassActive] = useState<boolean | undefined>(undefined);

	// Student tile screenshot loading lifecycle state.
	const screenshotStatus = useStudentState((state) => state.getScreenshotStatus(props.user.stableIdV1, props.classroom.id));
	const isStatusActive = screenshotStatus === StudentScreenshotStatus.ACTIVE || screenshotStatus === StudentScreenshotStatus.RESOLVED;
	const isStatusReporting =
		screenshotStatus === StudentScreenshotStatus.RETRIED || screenshotStatus === StudentScreenshotStatus.ISSUE_ONGOING;
	const setScreenshotStatus = useStudentState((state) => state.setScreenshotStatus);
	const [connectingTimeout, setConnectingTimeout] = useState<NodeJS.Timeout | null>(null);
	const [retryUsed, setRetryUsed] = useState(false);

	const toast = useToast();

	// Function to update the student's screenshot status, with error handling if the reporting fails.
	const updateScreenshotStatus = useCallback(
		(status: StudentScreenshotStatus, onFailCallback = () => {}) => {
			setScreenshotStatus(props.user, props.classroom.id, status, props.classOpenTime ?? 0).catch((error) => {
				console.error("Failed to set student's screenshot status:", error);
				toast.errorToast({
					title: "Error",
					description: "Failed to update student's status, please try again",
					isClosable: true,
				});
				onFailCallback();
			});
		},
		[props.classroom, props.user, props.classOpenTime, setScreenshotStatus, toast]
	);

	useEffect(() => {
		if (previousIsClassActive === props.isClassActive) {
			return;
		}

		setPreviousIsClassActive(props.isClassActive);

		if (props.isClassActive) {
			const timeoutCancel = setTimeout(() => {
				if (!hasWindowData && screenshotStatus === StudentScreenshotStatus.CONNECTING) {
					updateScreenshotStatus(StudentScreenshotStatus.NO_ACTIVITY);
				}
			}, envConfig.STUDENT_ACTIVITY_TIMEOUT_MILLIS);
			setConnectingTimeout(timeoutCancel);
		}

		return () => {
			if (connectingTimeout !== null) {
				clearTimeout(connectingTimeout);
				setConnectingTimeout(null);
			}
		};
	}, [props.isClassActive, hasWindowData, screenshotStatus, updateScreenshotStatus, connectingTimeout, previousIsClassActive]);

	const retryScreenshot = () => {
		if (
			(screenshotStatus === StudentScreenshotStatus.NO_ACTIVITY || screenshotStatus === StudentScreenshotStatus.DISCONNECTED) &&
			!retryUsed
		) {
			// Prevent multiple retries, before we have entered the RETRIED state.
			setRetryUsed(true);
			// Send a targeted start class event to the student, then set the state to RETRIED to trigger the retry animation,
			// and prevent multiple retries.
			updateScreenshotStatus(StudentScreenshotStatus.RETRIED, () => setRetryUsed(false));
		}
	};

	const useIssueOngoing = () => {
		if (screenshotStatus === StudentScreenshotStatus.RETRIED) {
			updateScreenshotStatus(StudentScreenshotStatus.ISSUE_ONGOING);
		}
	};

	useEffect(() => {
		if (prevWindowData !== props.windowData && isScreenshotLoading) {
			if (!closedTabId.current || ("tab_id" in props.windowData && closedTabId.current !== props.windowData.tab_id)) {
				setIsScreenshotLoading(false);
			}
		}
	}, [props.windowData, props.view, isScreenshotLoading, prevView, prevWindowData]);

	const isClassActive = () => props.isClassActive;
	const isUserPaused = useClassroomPolicyStore.getState().getPausedStudents().includes(props.user.username);

	useEffect(() => {
		if (previousHasWindowData === hasWindowData) {
			return;
		}

		setPreviousHasWindowData(hasWindowData);

		if (hasWindowData) {
			// once the screenshot has been loaded -- clear the activity check timeout to prevent the retry
			// button from showing
			if (connectingTimeout !== null) {
				clearTimeout(connectingTimeout);
			}

			if (!isStatusActive) {
				// if the current student status is an inactive state, and we're not waiting to connect,
				// then set the status to RESOLVED, as we have received a screenshot
				if (screenshotStatus === StudentScreenshotStatus.CONNECTING) {
					updateScreenshotStatus(StudentScreenshotStatus.ACTIVE);
				} else if (isStatusReporting) {
					updateScreenshotStatus(StudentScreenshotStatus.RESOLVED);
				}
			}
		} else {
			// if we don't have a screen, and we are in the "RESOLVED" state, set state to DISCONNECTED so the teacher can re-retry
			if (isStatusActive) {
				updateScreenshotStatus(StudentScreenshotStatus.DISCONNECTED);
			}
		}
	}, [
		hasWindowData,
		previousHasWindowData,
		updateScreenshotStatus,
		screenshotStatus,
		connectingTimeout,
		isStatusActive,
		isStatusReporting,
	]);

	const forceCloseTab = (tabId: string, setLoading = false) => {
		if (!props.canCloseTab || !tabId) return;

		const split = tabId.split("_");

		Api.post(
			"/event/close-tab",
			{
				recipients: [props.user.username],
				event_object: {
					windowId: split[0],
					tabId: split[1],
				},
			},
			() => {
				if (setLoading) {
					if ("background_tabs" in props.windowData && props.windowData.background_tabs && props.windowData.background_tabs.length) {
						closedTabId.current = tabId;
						setIsScreenshotLoading(true);
					}
				}
			}
		);
	};

	const openZenDeskWaitingForStudentActivity = () => {
		window.open("https://help.classwize.net/hc/en-gb/articles/6227484666652-Waiting-for-Student-Activity", "_blank");
	};

	const renderContentView = () => {
		if (!isClassActive()) {
			return (
				<div className="StudentTileContent_offline">
					<p>Start class to see student activity...</p>
					<button onClick={openZenDeskWaitingForStudentActivity} rel="noopener noreferrer">
						<img className="ClassroomSession_tipIcon" src={iconHelpBlue} alt="" />
						Find out more
					</button>
				</div>
			);
		}

		if (!hasWindowData) {
			if (!(deviceId && envConfig.SCREEN_VISIBILITY_IMPROVEMENTS_ENABLED_DEVICES.includes(deviceId)) || props.disableScreenshotRetry) {
				return (
					<div className="StudentTileContent_offline">
						<p>Waiting for student activity...</p>
						<button onClick={openZenDeskWaitingForStudentActivity} rel="noopener noreferrer">
							<img className="ClassroomSession_tipIcon" src={iconHelpBlue} alt="" />
							Find out more
						</button>
					</div>
				);
			}

			return (
				<InactiveStudentWindow
					connecting={screenshotStatus === StudentScreenshotStatus.CONNECTING}
					screenshotRetryUsed={
						screenshotStatus === StudentScreenshotStatus.RETRIED || screenshotStatus === StudentScreenshotStatus.ISSUE_ONGOING
					}
					issueOngoingUsed={screenshotStatus === StudentScreenshotStatus.ISSUE_ONGOING}
					useScreenshotRetry={retryScreenshot}
					useIssueOngoing={useIssueOngoing}
				/>
			);
		}

		if (hasWindowData) {
			if (props.view === "screens" && Object.keys(props.windowData).length) {
				return (
					<div className="StudentTileContent_screenContent">
						<StudentTileScreenshot
							user={props.user}
							windowData={props.windowData as WindowData}
							signatureDb={props.signatureDb}
							forceCloseTab={forceCloseTab}
							canCloseTab={props.canCloseTab}
							isScreenshotLoading={isScreenshotLoading}
							classIsActive={props.isClassActive}
							isMonitorMode={props.classroom.session?.isMonitoring ?? false}
							openP2pModal={props.openP2pModal}
							tabItemLimit={props.tabItemLimit}
							isRestricted={isBeingRestricted(props.windowData as WindowData)}
							inScreenshare={screenshareState.screenshareStudentUsernames.has(props.user.username)}
						/>
					</div>
				);
			}
			if (props.view === "tabs") {
				return (
					<StudentTileTabView
						windowData={props.windowData as WindowData}
						signatureDb={props.signatureDb}
						user={props.user}
						tabItemLimit={props.tabItemLimit}
						canCloseTab={props.canCloseTab}
						isClassActive={props.isClassActive}
						isMonitoring={props.classroom.session?.isMonitoring ?? false}
						forceCloseTab={forceCloseTab}
						openP2pModal={props.openP2pModal}
					/>
				);
			}
		}

		return (
			<div className="StudentTileContent_offline">
				<p>Unknown error...</p>
			</div>
		);
	};

	return (
		<div className="StudentTileContent">
			{isUserPaused && (
				<Box
					background={"#FFDBDF"}
					rounded={"md"}
					textAlign={"center"}
					py={0.5}
					fontSize={"sm"}
					mb={2}
					fontWeight={500}
					position={"absolute"}
					top={"16px"}
					left={"50%"}
					transform={"translate(-50%, -50%)"}
					width={"94%"}
				>
					Internet Paused
				</Box>
			)}
			{renderContentView()}
		</div>
	);
};
